import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";

// Color/label maps for performance_group and top_ten_items modes
export const GROUP_COLORS = {
  Star: "#0AB39C",
  "Cash Cow": "#F7B84B",
  Puzzle: "#F3F6F9",
  Dog: "#F06548",
  "Not Sold": "#383E42",
};

export const GROUP_INFO = {
  Star: { label: "Star" },
  "Cash Cow": { label: "Cash Cow" },
  Puzzle: { label: "Puzzle" },
  Dog: { label: "Dog" },
  "Not Sold": { label: "Not Sold" },
};

export const TOP_TEN_COLORS = {
  "Top 5": "#0AB39C",
  "Top 6-10": "#F7B84B",
  "Outside Top 10": "#F3F6F9",
  "Not Sold": "#383E42",
};

export const TOP_TEN_GROUP_INFO = {
  "Top 5": { label: "Top 5" },
  "Top 6-10": { label: "Top 6-10" },
  "Outside Top 10": { label: "Outside Top 10" },
  "Not Sold": { label: "Not Sold" },
};

const PerformanceGroupHeatmap = ({
  data = [],
  rows = [],
  showLegend = true,
  showDateHeader = true,
  weekMarkers = null,
  bare = false,
  mode = "performance_group",
  title = "",
  subtitle = "",
  onCellClick = null,
  height = 30,
  showTooltip = true,
  onDateClick = null,
}) => {
  const containerRef = useRef(null);
  const [cellSize, setCellSize] = useState(20);
  const [cellGap, setCellGap] = useState(2);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Which color/label set is active for this render
  const activeColors = mode === "top_ten_items" ? TOP_TEN_COLORS : GROUP_COLORS;
  const activeInfo = mode === "top_ten_items" ? TOP_TEN_GROUP_INFO : GROUP_INFO;
  const timelineRows = useMemo(
    () => (rows.length ? rows : [{ title, subtitle, timeline: data }]),
    [rows, title, subtitle, data],
  );
  const timelineData = useMemo(
    () => timelineRows[0]?.timeline || [],
    [timelineRows],
  );

  // Format date: 28Jun -> 28-Jun
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{2}[A-Za-z]{3}$/.test(dateStr)) {
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 5);
      return `${day}-${month}`;
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${day}-${month}`;
  };

  // Full date for tooltip
  const formatFullDate = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{2}[A-Za-z]{3}$/.test(dateStr)) {
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 5);
      return `${day}-${month}`;
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  };

  // Calculate cell size based on available width
  const dateLabelPad = 32;
  const labelWidth = 160;
  const minCellGap = 2;
  const calculateLayout = useCallback(() => {
    if (!containerRef.current || !timelineData || timelineData.length === 0) {
      return { size: 20, gap: minCellGap };
    }
    const styles = window.getComputedStyle(containerRef.current);
    const paddingX =
      (parseFloat(styles.paddingLeft) || 0) +
      (parseFloat(styles.paddingRight) || 0);
    const containerWidth = Math.max(
      0,
      containerRef.current.clientWidth - paddingX - labelWidth - dateLabelPad,
    );
    const dataLength = timelineData.length;
    const totalMinGaps = Math.max(0, dataLength - 1) * minCellGap;
    const idealSize = (containerWidth - totalMinGaps) / dataLength;
    const minSize = 8;
    const maxSize = 30;
    const size = Math.floor(
      Math.min(
        Math.max(idealSize, window.innerWidth < 768 ? 6 : minSize),
        maxSize,
      ),
    );
    const leftover = containerWidth - size * dataLength;
    const gap =
      dataLength > 1
        ? Math.max(minCellGap, leftover / (dataLength - 1))
        : minCellGap;
    return { size, gap };
  }, [timelineData]);

  // Handle resize and data changes
  useEffect(() => {
    const updateSize = () => {
      const { size, gap } = calculateLayout();
      setCellSize(size);
      setCellGap(gap);
      setIsSmallScreen(window.innerWidth < 768);
    };
    updateSize();
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        updateSize();
      });
      resizeObserver.observe(containerRef.current);
      return () => {
        resizeObserver.disconnect();
      };
    }
    window.addEventListener("resize", updateSize);
    return () => {
      window.removeEventListener("resize", updateSize);
    };
  }, [calculateLayout]);

  // Recalculate when data changes
  useEffect(() => {
    const { size, gap } = calculateLayout();
    setCellSize(size);
    setCellGap(gap);
  }, [calculateLayout]);

  // Font size based on cell size
  const getFontSize = () => {
    if (isSmallScreen) return "8px";
    if (cellSize > 22) return "12px";
    if (cellSize > 18) return "11px";
    if (cellSize > 14) return "10px";
    return "9px";
  };

  // Get stats for the active color/label set
  const getGroupStats = () => {
    const stats = {};
    Object.keys(activeColors).forEach((key) => {
      stats[key] = 0;
    });
    timelineRows.forEach((row) => {
      row.timeline.forEach((item) => {
        const group = item.performance_group || "Not Sold";
        if (stats[group] !== undefined) stats[group]++;
      });
    });
    return stats;
  };

  // Handle cell click
  const handleCellClick = (item, index) => {
    if (onCellClick) onCellClick(item, index);
    if (onDateClick) onDateClick(item.row_dt, item);
  };

  // Empty state
  if (!timelineRows.length || !timelineData.length) {
    return (
      <div
        style={{
          padding: "30px 20px",
          textAlign: "center",
          color: "#6b7280",
          background: "#f9fafb",
          borderRadius: "8px",
          border: "1px dashed #d1d5db",
        }}
      >
        <div style={{ fontSize: "14px" }}>No performance data available</div>
        <div style={{ fontSize: "12px", marginTop: "4px", color: "#9ca3af" }}>
          Select a view to load data
        </div>
      </div>
    );
  }

  const groupStats = getGroupStats();
  const hasAnyStat = Object.keys(groupStats).some((key) => groupStats[key] > 0);

  return (
    <div
      ref={containerRef}
      style={{
        background: "white",
        borderRadius: bare ? 0 : "8px",
        padding: bare ? "6px 8px 6px 8px" : "16px 28px 12px 8px",
        width: "100%",
        maxWidth: "100%",
        overflow: "visible",
        position: "relative",
        border: bare ? "none" : "1px solid #e5e7eb",
        boxShadow: bare ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      {/* Title Section */}
      {(title || subtitle) && (
        <div style={{ marginBottom: "8px", padding: "0 4px" }}>
          {title && (
            <h3
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#111827",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {title}
              {!bare && hasAnyStat && (
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: "400",
                    color: "#6b7280",
                    background: "#f3f4f6",
                    padding: "2px 8px",
                    borderRadius: "10px",
                  }}
                >
                  {data.length} days
                </span>
              )}
            </h3>
          )}
          {subtitle && (
            <div
              style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}
            >
              {subtitle}
            </div>
          )}
        </div>
      )}

      {/* Stats Summary - Quick view of distribution */}
      {!bare && hasAnyStat && (
        <div
          style={{
            display: "flex",
            gap: "6px",
            marginBottom: "10px",
            padding: "0 4px",
            flexWrap: "wrap",
          }}
        >
          {Object.entries(activeColors).map(([group, color]) => {
            return (
              <div
                key={group}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "11px",
                  color: "#374151",
                }}
              >
                <span
                  style={{
                    width: "14px",
                    height: "14px",
                    borderRadius: "2px",
                    backgroundColor: color,
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                />
                <span>{group}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Legend */}
      {showLegend && (
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "10px",
            paddingBottom: "10px",
            borderBottom: "1px solid #f0f0f0",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {Object.entries(activeColors).map(([group, color]) => {
            const info = activeInfo[group] || activeInfo["Not Sold"];
            return (
              <div
                key={group}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "10px",
                  color: "#374151",
                }}
              >
                <span
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "2px",
                    backgroundColor: color,
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                />
                <span>{info.icon}</span>
                <span>{group}</span>
              </div>
            );
          })}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "10px",
              color: "#374151",
            }}
          >
            <span
              style={{
                width: "14px",
                height: "14px",
                borderRadius: "2px",
                backgroundColor: "#fff",
                border: "1px solid #d1d5db",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxSizing: "border-box",
              }}
            >
              <span
                style={{
                  width: "6px",
                  height: "6px",
                  backgroundColor: "#1f2937",
                  display: "block",
                }}
              />
            </span>
            <span>Number 1 of the day</span>
          </div>
        </div>
      )}

      {/* Date Header */}
      {showDateHeader && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${labelWidth}px repeat(${timelineData.length}, ${cellSize}px)`,
            columnGap: `${cellGap}px`,
            paddingLeft: "4px",
            height: isSmallScreen ? "24px" : "32px",
            marginBottom: "4px",
            overflow: "visible",
            alignItems: "end",
          }}
        >
          <div />
          {timelineData.map((item, index) => {
            const date = item.row_dt || "";
            const fontSize = getFontSize();
            const isLast = index === timelineData.length - 1;
            const showLabel =
              !weekMarkers || weekMarkers.includes(date) || isLast;
            return (
              <div
                key={`header-${index}`}
                style={{
                  width: `${cellSize}px`,
                  boxSizing: "border-box",
                  textAlign: isLast ? "right" : "center",
                  fontSize: fontSize,
                  color: "#6b7280",
                  fontWeight: "600",
                  whiteSpace: "nowrap",
                  overflow: "visible",
                  position: "relative",
                  zIndex: isLast ? 2 : 1,
                  transformOrigin: "bottom center",
                  marginTop: "20px",
                  paddingBottom: "2px",
                  cursor: onDateClick ? "pointer" : "default",
                }}
                onClick={() => {
                  if (onDateClick) onDateClick(item.row_dt, item);
                }}
              >
                {showLabel ? formatDate(date) : ""}
              </div>
            );
          })}
        </div>
      )}

      {/* Performance/Rank Cells */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          padding: "2px 0",
          overflow: "visible",
        }}
      >
        {timelineRows.map((row, rowIndex) => (
          <div
            key={`row-${rowIndex}`}
            style={{
              display: "grid",
              gridTemplateColumns: `${labelWidth}px repeat(${timelineData.length}, ${cellSize}px)`,
              columnGap: `${cellGap}px`,
              alignItems: "center",
              paddingBottom: "10px",
              paddingLeft: "4px",
            }}
          >
            <div
              style={{
                minWidth: 0,
                lineHeight: 1.2,
              }}
            >
              <div style={{ fontSize: "14px", color: "#111827" }}>
                {row.title || ""}
              </div>
              <div style={{ fontSize: "11px", color: "#6b7280" }}>
                {row.subtitle || ""}
              </div>
            </div>
              {row.timeline.map((item, index) => {
                const group = item.performance_group || "Not Sold";
                const color = activeColors[group] || activeColors["Not Sold"];
                const date = item.row_dt || "";
                const info = activeInfo[group] || activeInfo["Not Sold"];
                const itemName = item.item_name || item.name || "";
                const category = item.category || "";
                const soldCount = item.sold_count || item.sold || 0;
                const isWinner =
                  group !== "Not Sold" &&
                  soldCount > 0 &&
                  String(item.winner || "").toLowerCase() === "yes";
                const markerSize = 6;
                const boxHeight = Number(height) || 30;
                const markerLeft = Math.floor((cellSize - markerSize) / 2);
                const markerTop = Math.floor((boxHeight - markerSize) / 2);

                return (
                  <div
                    key={`cell-${rowIndex}-${index}`}
                    style={{
                      width: `${cellSize}px`,
                      height: `${boxHeight}px`,
                      position: "relative",
                      cursor: onCellClick ? "pointer" : "default",
                      backgroundColor: color,
                      backgroundImage: isWinner
                        ? "linear-gradient(#1f2937, #1f2937)"
                        : "none",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: `${markerSize}px ${markerSize}px`,
                      backgroundPosition: `${markerLeft}px ${markerTop}px`,
                      borderRadius: "3px",
                      boxSizing: "border-box",
                    }}
                    onMouseEnter={(e) => {
                      setHoveredCell(`${rowIndex}-${index}`);
                      const rect = e.currentTarget.getBoundingClientRect();
                      setTooltipPosition({
                        x: rect.left + rect.width / 2,
                        y: rect.top,
                      });
                    }}
                    onMouseLeave={() => setHoveredCell(null)}
                    onClick={() => handleCellClick(item, index)}
                  >
                    {/* Tooltip */}
                    {showTooltip && hoveredCell === `${rowIndex}-${index}` && (
                      <div
                        style={{
                          position: "fixed",
                          zIndex: 10001,
                          bottom: `${window.innerHeight - tooltipPosition.y + 8}px`,
                          left: `${tooltipPosition.x}px`,
                          transform: "translateX(-50%)",
                          minWidth: "180px",
                          maxWidth: "260px",
                          padding: "10px 14px",
                          backgroundColor: "#fff",
                          color: "#2e2e2e",
                          borderRadius: "8px",
                          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                          fontSize: "12px",
                          lineHeight: "1.5",
                          textAlign: "left",
                          pointerEvents: "none",
                        }}
                      >
                        {isWinner && (
                          <span
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              width: "6px",
                              height: "6px",
                              backgroundColor: "#1f2937",
                            }}
                          />
                        )}
                        <div
                          style={{
                            fontWeight: "600",
                            color: "#111827",
                            marginBottom: "4px",
                            fontSize: "13px",
                          }}
                        >
                          {formatFullDate(date)}
                        </div>

                        {itemName && (
                          <div style={{ color: "#2d323a" }}>
                            {itemName}
                            {category && (
                              <span
                                style={{ marginLeft: "4px", color: "#6b7280" }}
                              >
                                · {category}
                              </span>
                            )}
                          </div>
                        )}

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            marginTop: "4px",
                          }}
                        >
                          <span>{info.icon}</span>

                          <span style={{ color: "#1f2937" }}>
                            {group}
                            {item.rank ? ` (#${item.rank})` : ""}
                          </span>

                          {soldCount > 0 && (
                            <span
                              style={{ color: "#6b7280", fontSize: "11px" }}
                            >
                              · {soldCount} sold
                            </span>
                          )}
                        </div>

                        <div
                          style={{
                            fontSize: "10px",
                            color: "#6b7280",
                            marginTop: "2px",
                          }}
                        >
                          {info.description}
                        </div>

                        {/* Tooltip arrow */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: "-5px",
                            left: "50%",
                            transform: "translateX(-50%) rotate(45deg)",
                            width: "10px",
                            height: "10px",
                            backgroundColor: "#fff",
                          }}
                        />
                      </div>
                    )}{" "}
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
};

PerformanceGroupHeatmap.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      row_dt: PropTypes.string,
      performance_group: PropTypes.string,
      item_name: PropTypes.string,
      category: PropTypes.string,
      sold_count: PropTypes.number,
      winner: PropTypes.string,
      name: PropTypes.string,
      sold: PropTypes.number,
      rank: PropTypes.number,
    }),
  ),
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
      timeline: PropTypes.array,
    }),
  ),
  showLegend: PropTypes.bool,
  showDateHeader: PropTypes.bool,
  weekMarkers: PropTypes.arrayOf(PropTypes.string),
  bare: PropTypes.bool,
  mode: PropTypes.oneOf(["performance_group", "top_ten_items"]),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onCellClick: PropTypes.func,
  onDateClick: PropTypes.func,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showTooltip: PropTypes.bool,
};

PerformanceGroupHeatmap.defaultProps = {
  data: [],
  showLegend: true,
  showDateHeader: true,
  weekMarkers: null,
  bare: false,
  mode: "performance_group",
  title: "",
  subtitle: "",
  onCellClick: null,
  onDateClick: null,
  height: 30,
  showTooltip: true,
};

export default PerformanceGroupHeatmap;
